import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Check, Copy, Trash, TrendUp, VideoCamera, X, Play, Hash, MusicNote, PaperPlaneTilt, Globe, Link, PencilSimple, FloppyDisk } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import AppLayout from '@/components/AppLayout';
import BackButton from '@/components/BackButton';
import BoostModal from '@/components/BoostModal';
import { useTheme } from '@/contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { value: 'reels', label: 'Instagram Reels', emoji: '📸' },
  { value: 'tiktok', label: 'TikTok', emoji: '🎵' },
  { value: 'youtube_shorts', label: 'YouTube Shorts', emoji: '▶️' },
  { value: 'stories', label: 'Stories', emoji: '⏱️' },
];

const OBJECTIVES = [
  { value: 'trafic', label: 'Générer du trafic', desc: 'Attirer des visiteurs sur la page' },
  { value: 'conversion', label: 'Convertir en clients', desc: 'Pousser à l\'action' },
];

export default function SEOPages() {
  const { isDark } = useTheme();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [boostModal, setBoostModal] = useState({ open: false, page: null, data: null });
  const [boosting, setBoosting] = useState({});
  
  // State pour le modal vidéo
  const [videoModal, setVideoModal] = useState({ open: false, page: null });
  const [videoSettings, setVideoSettings] = useState({ platform: 'reels', objective: 'trafic' });
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [generatedVideo, setGeneratedVideo] = useState(null);
  
  // State pour l'édition d'URL
  const [editingUrl, setEditingUrl] = useState(null); // page_id de la page en cours d'édition
  const [urlInput, setUrlInput] = useState('');
  const [savingUrl, setSavingUrl] = useState({});

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/seo/pages`, {
        withCredentials: true,
      });
      setPages(response.data.pages);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (pageId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/seo/pages/${pageId}/publish`,
        {},
        { withCredentials: true }
      );
      toast.success('Page publiée ! Elle est maintenant visible sur Google');
      fetchPages();
    } catch (error) {
      toast.error('Erreur lors de la publication');
    }
  };

  const copyPublicUrl = (slug) => {
    const url = `${BACKEND_URL}/api/seo/public/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copiée !');
  };

  // Fonction pour sauvegarder l'URL de destination
  const handleSaveUrl = async (pageId) => {
    setSavingUrl({ ...savingUrl, [pageId]: true });
    try {
      await axios.put(
        `${BACKEND_URL}/api/seo/pages/${pageId}/url`,
        { destination_url: urlInput },
        { withCredentials: true }
      );
      toast.success('URL de destination enregistrée !');
      setEditingUrl(null);
      setUrlInput('');
      fetchPages();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingUrl({ ...savingUrl, [pageId]: false });
    }
  };

  const startEditingUrl = (page) => {
    setEditingUrl(page.page_id);
    setUrlInput(page.destination_url || '');
  };

  const cancelEditingUrl = () => {
    setEditingUrl(null);
    setUrlInput('');
  };

  const handleBoost = async (page) => {
    setBoosting({ ...boosting, [page.page_id]: true });
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/seo/pages/${page.page_id}/boost`,
        {},
        { withCredentials: true }
      );
      setBoostModal({ open: true, page, data: response.data });
      toast.success('Contenu de boost généré !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setBoosting({ ...boosting, [page.page_id]: false });
    }
  };

  const openVideoModal = (page) => {
    setVideoModal({ open: true, page });
    setGeneratedVideo(null);
    setVideoSettings({ platform: 'reels', objective: 'trafic' });
  };

  const closeVideoModal = () => {
    setVideoModal({ open: false, page: null });
    setGeneratedVideo(null);
  };

  const generateVideoFromSEO = async () => {
    if (!videoModal.page) return;
    
    setGeneratingVideo(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/video/from-seo/${videoModal.page.page_id}`,
        videoSettings,
        { withCredentials: true }
      );
      setGeneratedVideo(response.data);
      toast.success('Script vidéo généré !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setGeneratingVideo(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié !`);
  };

  const copyFullVideoScript = () => {
    if (!generatedVideo) return;
    const fullScript = `🎬 SCRIPT VIDÉO - ${generatedVideo.source_page_title}

📱 ${generatedVideo.platform_name} | ⏱️ ${generatedVideo.duration} | 🎯 ${generatedVideo.objective === 'trafic' ? 'Trafic' : 'Conversion'}

🎯 ACCROCHE:
${generatedVideo.hook}

📝 CORPS:
${generatedVideo.body}

🔥 APPEL À L'ACTION:
${generatedVideo.call_to_action}

🎨 SUGGESTIONS VISUELLES:
${generatedVideo.visual_suggestions?.join('\n')}

📝 TEXTE BIO:
${generatedVideo.bio_text}

${generatedVideo.hashtags?.join(' ')}`;
    
    navigator.clipboard.writeText(fullScript);
    toast.success('Script complet copié !');
  };

  const filteredPages = pages.filter((page) => {
    if (filter === 'published') return page.status === 'published';
    if (filter === 'draft') return page.status === 'draft';
    return true;
  });

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="relative">
            <div className="w-14 h-14 rounded-full border-2 border-violet-500/20"></div>
            <div className="absolute inset-0 w-14 h-14 rounded-full border-2 border-violet-500 border-t-transparent animate-spin"></div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-4 sm:p-8 relative z-10" data-testid="seo-pages-page">
        {/* Back Button */}
        <BackButton to="/dashboard" label="Retour au tableau de bord" className="mb-4" />
        
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mb-6 sm:mb-8"
        >
          <h1 className={`text-3xl sm:text-4xl md:text-5xl mb-2 sm:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Mes Pages SEO</h1>
          <p className={`text-base sm:text-lg ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Gérez et publiez vos pages pour attirer des clients via Google</p>
        </motion.div>

        {/* Filters - Scrollable on mobile */}
        <div className="flex items-center gap-2 sm:gap-3 mb-6 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:overflow-visible">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl transition-all whitespace-nowrap text-sm sm:text-base ${
              filter === 'all' 
                ? 'bg-gradient-to-r from-violet-500/20 to-pink-500/15 text-violet-600 dark:text-white border border-violet-500/30' 
                : isDark 
                  ? 'bg-white/[0.04] text-white/60 hover:bg-white/[0.08] hover:text-white/80'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
            }`}
          >
            Toutes ({pages.length})
          </button>
          <button
            onClick={() => setFilter('published')}
            className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl transition-all whitespace-nowrap text-sm sm:text-base ${
              filter === 'published' 
                ? 'bg-gradient-to-r from-violet-500/20 to-pink-500/15 text-violet-600 dark:text-white border border-violet-500/30' 
                : isDark 
                  ? 'bg-white/[0.04] text-white/60 hover:bg-white/[0.08] hover:text-white/80'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
            }`}
          >
            Publiées ({pages.filter((p) => p.status === 'published').length})
          </button>
          <button
            onClick={() => setFilter('draft')}
            className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl transition-all whitespace-nowrap text-sm sm:text-base ${
              filter === 'draft' 
                ? 'bg-gradient-to-r from-violet-500/20 to-pink-500/15 text-violet-600 dark:text-white border border-violet-500/30' 
                : isDark 
                  ? 'bg-white/[0.04] text-white/60 hover:bg-white/[0.08] hover:text-white/80'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
            }`}
          >
            Brouillons ({pages.filter((p) => p.status === 'draft').length})
          </button>
        </div>

        {filteredPages.length === 0 ? (
          <div className={`p-12 text-center rounded-2xl ${isDark ? 'glass-card' : 'bg-white border border-gray-100 shadow-sm'}`}>
            <FileText size={48} weight="duotone" className="text-violet-400 mx-auto mb-4" />
            <p className={isDark ? 'text-white/50' : 'text-gray-500'}>Aucune page pour le moment</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredPages.map((page, index) => (
              <motion.div
                key={page.page_id}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.05 }}
                className={`p-4 sm:p-6 rounded-2xl ${isDark ? 'glass-card' : 'bg-white border border-gray-100 shadow-sm'}`}
              >
                {/* Header avec titre et status */}
                <div className="flex items-start justify-between gap-2 mb-3">
                  <h3 className={`text-base sm:text-lg font-semibold flex-1 min-w-0 ${isDark ? 'text-white/90' : 'text-gray-900'}`}>
                    {page.title}
                  </h3>
                  <span
                    className={`text-xs px-2 py-1 rounded-full border shrink-0 ${
                      page.status === 'published'
                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                        : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                    }`}
                  >
                    {page.status === 'published' ? 'Publiée' : 'Brouillon'}
                  </span>
                </div>
                
                {/* Description */}
                <p className={`text-sm mb-3 line-clamp-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{page.meta_description}</p>
                
                {/* Métadonnées */}
                <div className={`flex flex-wrap items-center gap-2 sm:gap-4 text-xs mb-4 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                  <span>📍 {page.city}</span>
                  <span>👁️ {page.views} vues</span>
                  <span className="hidden sm:inline">🏷️ {page.keywords.slice(0, 2).join(', ')}</span>
                </div>

                {/* Actions - Responsive Grid */}
                <div className="flex flex-wrap gap-2">
                  {page.status === 'draft' && (
                    <Button
                      onClick={() => handlePublish(page.page_id)}
                      className="flex-1 sm:flex-none"
                      data-testid={`publish-page-${page.page_id}`}
                    >
                      <Check className="mr-1 sm:mr-2" size={16} />
                      Publier
                    </Button>
                  )}
                  {page.status === 'published' && (
                    <>
                      <Button
                        onClick={() => openVideoModal(page)}
                        variant="outline"
                        size="sm"
                        className="border-accent/50 hover:bg-accent/10 text-xs sm:text-sm"
                        data-testid={`video-page-${page.page_id}`}
                      >
                        <VideoCamera className="mr-1" size={14} weight="duotone" />
                        <span className="hidden sm:inline">Créer une </span>vidéo
                      </Button>
                      <Button
                        onClick={() => handleBoost(page)}
                        disabled={boosting[page.page_id]}
                        size="sm"
                        className="bg-gradient-to-r from-primary to-accent text-xs sm:text-sm"
                        data-testid={`boost-page-${page.page_id}`}
                      >
                        <TrendUp className="mr-1" size={14} weight="bold" />
                        {boosting[page.page_id] ? '...' : 'Booster'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyPublicUrl(page.slug)}
                        className="text-xs sm:text-sm"
                        data-testid={`copy-url-${page.page_id}`}
                      >
                        <Copy className="mr-1" size={14} />
                        URL
                      </Button>
                    </>
                  )}
                </div>

                {/* Section URL de destination - Visible uniquement pour les pages publiées */}
                {page.status === 'published' && (
                  <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Globe size={16} className="text-violet-500" weight="duotone" />
                      <span className={`text-xs font-medium ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                        URL de redirection (votre site web)
                      </span>
                    </div>
                    
                    {editingUrl === page.page_id ? (
                      <div className="flex gap-2">
                        <Input
                          type="url"
                          placeholder="https://votresite.com"
                          value={urlInput}
                          onChange={(e) => setUrlInput(e.target.value)}
                          className={`flex-1 text-sm ${isDark ? 'bg-white/5 border-white/10' : ''}`}
                          data-testid={`url-input-${page.page_id}`}
                        />
                        <Button
                          size="sm"
                          onClick={() => handleSaveUrl(page.page_id)}
                          disabled={savingUrl[page.page_id]}
                          className="bg-emerald-500 hover:bg-emerald-600"
                          data-testid={`save-url-${page.page_id}`}
                        >
                          <FloppyDisk size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={cancelEditingUrl}
                        >
                          <X size={14} />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        {page.destination_url ? (
                          <a 
                            href={page.destination_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-violet-500 hover:text-violet-400 truncate max-w-[200px] sm:max-w-[300px]"
                          >
                            {page.destination_url}
                          </a>
                        ) : (
                          <span className={`text-sm italic ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                            Aucune URL - les visiteurs ne seront pas redirigés
                          </span>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => startEditingUrl(page)}
                          className="ml-auto"
                          data-testid={`edit-url-${page.page_id}`}
                        >
                          <PencilSimple size={14} className="mr-1" />
                          {page.destination_url ? 'Modifier' : 'Ajouter URL'}
                        </Button>
                      </div>
                    )}
                    
                    <p className={`text-xs mt-2 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                      Les visiteurs de la page SEO verront un bouton "Visiter notre site" qui les redirigera vers cette URL
                    </p>
                  </div>
                )}

                {/* Lien page publique - Supprimé car page blanche */}
              </motion.div>
            ))}
          </div>
        )}

        {/* CTA bottom */}
        {pages.length > 0 && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className={`mt-8 rounded-xl p-6 text-center ${isDark ? 'glass' : 'bg-violet-50 border border-violet-100'}`}
          >
            <p className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Plus vous publiez, plus vous attirez de clients</p>
            <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              Chaque page publiée = plus de visibilité sur Google = plus de clients potentiels
            </p>
          </motion.div>
        )}
      </div>
      
      {/* Boost Modal */}
      {boostModal.open && (
        <BoostModal
          page={boostModal.page}
          boostData={boostModal.data}
          onClose={() => setBoostModal({ open: false, page: null, data: null })}
        />
      )}

      {/* Video Modal */}
      <AnimatePresence>
        {videoModal.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeVideoModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <VideoCamera className="text-accent" weight="duotone" />
                    Créer une vidéo promotionnelle
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Pour : {videoModal.page?.title}
                  </p>
                </div>
                <button
                  onClick={closeVideoModal}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {!generatedVideo ? (
                <div className="space-y-6">
                  <div className="p-4 rounded-lg bg-accent/10 border border-accent/20">
                    <p className="text-sm">
                      Transformez votre page SEO en contenu viral pour les réseaux sociaux. 
                      Le script sera optimisé pour promouvoir votre page et générer du trafic.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Plateforme</label>
                      <Select 
                        value={videoSettings.platform} 
                        onValueChange={(v) => setVideoSettings({ ...videoSettings, platform: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PLATFORMS.map((p) => (
                            <SelectItem key={p.value} value={p.value}>
                              {p.emoji} {p.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-2 block">Objectif</label>
                      <Select 
                        value={videoSettings.objective} 
                        onValueChange={(v) => setVideoSettings({ ...videoSettings, objective: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {OBJECTIVES.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button
                    onClick={generateVideoFromSEO}
                    disabled={generatingVideo}
                    className="w-full bg-gradient-to-r from-accent to-primary"
                    data-testid="generate-video-from-seo-btn"
                  >
                    {generatingVideo ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Génération du script...
                      </>
                    ) : (
                      <>
                        <VideoCamera className="mr-2" weight="duotone" />
                        Générer le script vidéo
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      <span className="text-xs px-2 py-1 rounded-full bg-accent/20 text-accent">
                        {generatedVideo.platform_name}
                      </span>
                      <span className="text-xs px-2 py-1 rounded-full bg-primary/20 text-primary">
                        {generatedVideo.duration}
                      </span>
                      <span className="text-xs px-2 py-1 rounded-full bg-white/10">
                        {generatedVideo.objective === 'trafic' ? '🎯 Trafic' : '💰 Conversion'}
                      </span>
                    </div>
                    <Button variant="outline" size="sm" onClick={copyFullVideoScript}>
                      <Copy className="mr-1" size={14} />
                      Copier tout
                    </Button>
                  </div>

                  {/* Accroche */}
                  <div className="p-4 rounded-lg bg-gradient-to-r from-accent/10 to-primary/10 border border-accent/20">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-accent">🎯 Accroche</h3>
                      <button
                        onClick={() => copyToClipboard(generatedVideo.hook, 'Accroche')}
                        className="text-muted-foreground hover:text-accent transition-colors"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                    <p className="text-sm">{generatedVideo.hook}</p>
                  </div>

                  {/* Corps */}
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold">📝 Corps du script</h3>
                      <button
                        onClick={() => copyToClipboard(generatedVideo.body, 'Corps')}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                    <p className="text-sm whitespace-pre-line">{generatedVideo.body}</p>
                  </div>

                  {/* CTA */}
                  <div className="p-4 rounded-lg bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-primary">🔥 Appel à l'action</h3>
                      <button
                        onClick={() => copyToClipboard(generatedVideo.call_to_action, 'CTA')}
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                    <p className="text-sm">{generatedVideo.call_to_action}</p>
                  </div>

                  {/* Bio text */}
                  {generatedVideo.bio_text && (
                    <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold flex items-center gap-2">
                          <PaperPlaneTilt size={14} className="text-primary" />
                          Texte pour la bio/description
                        </h3>
                        <button
                          onClick={() => copyToClipboard(generatedVideo.bio_text, 'Texte bio')}
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                      <p className="text-sm text-muted-foreground">{generatedVideo.bio_text}</p>
                    </div>
                  )}

                  {/* Visuels */}
                  {generatedVideo.visual_suggestions?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                        <Play size={14} className="text-accent" />
                        Suggestions visuelles
                      </h3>
                      <ul className="space-y-1">
                        {generatedVideo.visual_suggestions.map((visual, i) => (
                          <li key={i} className="text-sm text-muted-foreground">{visual}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Hashtags */}
                  {generatedVideo.hashtags?.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold flex items-center gap-2">
                          <Hash size={14} className="text-primary" />
                          Hashtags
                        </h3>
                        <button
                          onClick={() => copyToClipboard(generatedVideo.hashtags.join(' '), 'Hashtags')}
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {generatedVideo.hashtags.map((tag, i) => (
                          <span key={i} className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4 border-t border-white/10">
                    <Button
                      onClick={() => setGeneratedVideo(null)}
                      variant="outline"
                      className="flex-1"
                    >
                      Régénérer
                    </Button>
                    <Button
                      onClick={closeVideoModal}
                      className="flex-1"
                    >
                      Terminé
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AppLayout>
  );
}
